/**
 * Transforms between the engine metrics endpoint's wire shape and what the
 * Engine section draws (TRA-1484). Pure, so the cards' headline numbers,
 * their legends and their empty states are testable without a DOM.
 *
 * The gateway reads Baseten's metrics export every 2 minutes and buckets
 * the samples; every number here is its. Gauges arrive as fractions and
 * leave as percents. A null stays null: it is a gap in the line, because
 * a sample that was never taken (a model scaled to zero, a metric Baseten
 * does not send) is not a zero.
 */

import type { EnginePoint, ModelEngineMetricsResponse } from '@/api/generated/model'
import { HOUR_MS } from '@/components/models/home/replicaPan'
import type { TimeRange } from '@/components/models/home/replicaZoom'
import { formatTime } from '@/components/models/home/scalingWindow'

export type { EnginePoint, ModelEngineMetricsResponse }

/** The "Collected since" and "no data yet" dates read in Pacific, like the rest of the page. */
const PACIFIC = 'America/Los_Angeles'

/** The day the gateway update that saves engine samples went live (PR #1125). */
export const COLLECTION_STARTED = 'Oct 15, 2026'

export const NO_ENGINE_DATA_YET = `No engine data yet. Collection started with the gateway update on ${COLLECTION_STARTED}; charts fill in from then.`

/** The engine numbers of one bucket: gauges in percent, requests as counts, rates in tokens per second. */
export interface EngineNumbers {
  gpuAvg: number | null
  gpuMax: number | null
  kvAvg: number | null
  kvMax: number | null
  running: number | null
  runningMax: number | null
  waiting: number | null
  acceptance: number | null
  efficiency: number | null
  acceptedTps: number | null
  emittedTps: number | null
}

export type EngineMetric = keyof EngineNumbers

export interface EngineBucket extends EngineNumbers {
  /** The bucket's start, ms since the epoch; the charts' x value. */
  epoch: number
  /** The readout and tick label, in the reader's zone. */
  label: string
}

export interface EngineView {
  hours: number
  buckets: EngineBucket[]
  /** The window's number per metric: the mean of the buckets that have one, or for a `*Max` metric the highest. */
  summary: EngineNumbers
  /** "Collected since Oct 15, 1:58 PM PT" when the window starts before the first sample; null when the whole window has data. */
  collectedSince: string | null
}

/** A fraction from the gateway as a percent; null stays null. */
export function toPercent(fraction: number | null): number | null {
  return fraction === null ? null : fraction * 100
}

/** The mean of the measured values; null when nothing was measured. */
export function mean(values: (number | null)[]): number | null {
  const known = values.flatMap((value) => (value === null ? [] : [value]))
  if (known.length === 0) return null
  return known.reduce((total, value) => total + value, 0) / known.length
}

/** The highest measured value; null when nothing was measured. */
export function peak(values: (number | null)[]): number | null {
  const known = values.flatMap((value) => (value === null ? [] : [value]))
  return known.length === 0 ? null : Math.max(...known)
}

export function toEngineBucket(point: EnginePoint, hours: number): EngineBucket {
  const epoch = new Date(point.bucket).getTime()
  return {
    epoch,
    label: formatTime(epoch, hours),
    gpuAvg: toPercent(point.gpu_util_avg),
    gpuMax: toPercent(point.gpu_util_max),
    kvAvg: toPercent(point.kv_cache_avg),
    kvMax: toPercent(point.kv_cache_max),
    running: point.requests_running,
    runningMax: point.requests_running_max,
    waiting: point.requests_waiting,
    acceptance: toPercent(point.spec_acceptance_rate),
    efficiency: toPercent(point.spec_efficiency),
    acceptedTps: point.spec_accepted_tokens_per_s,
    emittedTps: point.spec_emitted_tokens_per_s,
  }
}

function pick(buckets: EngineBucket[], metric: EngineMetric): (number | null)[] {
  return buckets.map((bucket) => bucket[metric])
}

function toSummary(buckets: EngineBucket[]): EngineNumbers {
  return {
    gpuAvg: mean(pick(buckets, 'gpuAvg')),
    gpuMax: peak(pick(buckets, 'gpuMax')),
    kvAvg: mean(pick(buckets, 'kvAvg')),
    kvMax: peak(pick(buckets, 'kvMax')),
    running: mean(pick(buckets, 'running')),
    runningMax: peak(pick(buckets, 'runningMax')),
    waiting: mean(pick(buckets, 'waiting')),
    acceptance: mean(pick(buckets, 'acceptance')),
    efficiency: mean(pick(buckets, 'efficiency')),
    acceptedTps: mean(pick(buckets, 'acceptedTps')),
    emittedTps: mean(pick(buckets, 'emittedTps')),
  }
}

/** "Oct 15, 1:58 PM PT" for the moment the gateway saved its first sample. */
function formatPacific(iso: string): string {
  const text = new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: PACIFIC,
  })
  return `${text} PT`
}

/**
 * The "Collected since" line, only when the window reaches back before the
 * first sample: the charts then start part way through, and the line says
 * why the left side is empty.
 */
export function collectedSince(firstSampleAt: string, hours: number, now: number): string | null {
  const windowStart = now - hours * 3_600_000
  if (windowStart >= new Date(firstSampleAt).getTime()) return null
  return `Collected since ${formatPacific(firstSampleAt)}`
}

/** The points whose bucket starts inside the stretch. */
function pointsIn(points: EnginePoint[], shown: TimeRange): EnginePoint[] {
  return points.filter((point) => {
    const epoch = new Date(point.bucket).getTime()
    return epoch >= shown.start && epoch < shown.end
  })
}

/**
 * Null until the gateway has saved a sample for this model: the section
 * then shows `NO_ENGINE_DATA_YET`. `shown` is the stretch the page shows
 * (TRA-1592), the window at now, a window moved back or a zoom: the read
 * covers a window back from now that reaches it, and the view keeps the
 * buckets inside the stretch, summed and labelled as a window of its
 * length ending where it ends.
 */
export function toEngineView(response: ModelEngineMetricsResponse, shown: TimeRange): EngineView | null {
  if (response.first_sample_at === null) return null
  const hours = (shown.end - shown.start) / HOUR_MS
  const buckets = pointsIn(response.points, shown).map((point) => toEngineBucket(point, hours))
  return {
    hours,
    buckets,
    summary: toSummary(buckets),
    collectedSince: collectedSince(response.first_sample_at, hours, shown.end),
  }
}

/** True when no bucket in the window measured this metric, so the legend leaves it out. */
export function isMissing(view: EngineView, metric: EngineMetric): boolean {
  return view.summary[metric] === null
}

/** True when no bucket in the window measured anything: a model scaled to zero the whole time. */
export function hasSamples(view: EngineView): boolean {
  return Object.values(view.summary).some((value) => value !== null)
}

/** What a legend says instead of a metric Baseten did not send. */
export function missingNote(label: string): string {
  return `Baseten does not send ${label} for this model.`
}

/** "2.5" for a mean request count, "4" for a peak; a dash where nothing was measured. */
export function formatCount(value: number | null): string {
  if (value === null) return '—'
  if (Number.isInteger(value)) return value.toLocaleString()
  return value.toFixed(1)
}
